import React from "react";
import { FeatherCheck } from "@subframe/core";
import { PlSummaryCard } from "./PlSummaryCard";
import { MonthAtGlanceInsightsPanel } from "./MonthAtGlanceInsightsPanel";
import type { MonthAtGlanceInsight } from "./MonthAtGlanceInsightsPanel";
import { Skeleton } from "../shared/Skeleton";
import { useMonthAtGlance } from "../../hooks/useMonthAtGlance";
import { useMonthAtGlanceInsights } from "../../hooks/useMonthAtGlanceInsights";

export interface MonthAtGlanceSectionProps {
  monthLabel?: string;
  booksClosed?: boolean;
  plSummary?: {
    totalAmount: string;
    changePercent: string;
    changeLabel?: string;
    revenueAmount: string;
    expensesAmount: string;
    ebitdaAmount: string;
  };
  insights?: MonthAtGlanceInsight[];
}

export function MonthAtGlanceSection() {
  const monthAtGlanceData = useMonthAtGlance();
  const insightsData = useMonthAtGlanceInsights({
    enabled:
      !monthAtGlanceData.loading &&
      !monthAtGlanceData.error &&
      !monthAtGlanceData.noClosedMonth,
  });

  const monthLabel = monthAtGlanceData.monthLabel;
  const booksClosed = monthAtGlanceData.booksClosed;
  const asOfBooksClosedLabel = monthAtGlanceData.asOfBooksClosedLabel;
  const plSummary = monthAtGlanceData.plSummary;
  const insights = insightsData.insights;

  return (
    <div className="flex w-full flex-col items-start gap-8 rounded-2xl bg-[#eef5f7] px-6 py-6">
      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-1 flex-none flex-col items-start rounded-full bg-success-600" />
            {monthAtGlanceData.loading && !monthLabel ? (
              <Skeleton className="h-5 w-40" />
            ) : (
              <span className="text-body-bold font-body-bold text-neutral-500 uppercase tracking-widest">
                {monthLabel}
              </span>
            )}
          </div>
          {!monthAtGlanceData.loading && booksClosed && (
            <div className="flex items-center gap-1.5 rounded-full bg-success-100 px-3 py-1">
              <FeatherCheck className="text-caption font-caption text-success-700" />
              <span className="text-caption-bold font-caption-bold text-success-700">
                Books closed
              </span>
            </div>
          )}
        </div>
        {!monthAtGlanceData.loading && booksClosed && asOfBooksClosedLabel ? (
          <p className="max-w-2xl text-caption font-caption text-neutral-600">
            {asOfBooksClosedLabel}
          </p>
        ) : null}
      </div>
      {!monthAtGlanceData.loading &&
        monthAtGlanceData.noClosedMonth &&
        monthAtGlanceData.message && (
          <div
            className="w-full rounded-lg bg-neutral-100 px-4 py-3"
            role="status"
          >
            <span className="text-caption font-caption text-neutral-700">
              {monthAtGlanceData.message}
            </span>
          </div>
        )}
      {monthAtGlanceData.error && (
        <div className="w-full rounded-lg bg-error-50 px-4 py-2" role="alert">
          <span className="text-caption font-caption text-error-600">
            {monthAtGlanceData.error}
          </span>
        </div>
      )}
      <div className="grid w-full grid-cols-1 items-stretch gap-6 md:grid-cols-[minmax(0,1fr)_24rem]">
        <PlSummaryCard {...plSummary} loading={monthAtGlanceData.loading} />
        {!monthAtGlanceData.loading && monthAtGlanceData.noClosedMonth ? (
          <div className="flex min-h-0 w-full flex-col gap-3">
            <div className="w-full rounded-2xl bg-white px-6 py-7 shadow-lg self-stretch">
              <div className="flex flex-col gap-2">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Insights
                </span>
                <span className="text-caption font-caption text-neutral-600">
                  {monthAtGlanceData.message ??
                    "Insights will appear once a month is closed in the month-end checklist."}
                </span>
              </div>
            </div>
          </div>
        ) : insightsData.error ? (
          <div className="flex min-h-0 w-full flex-col gap-3">
            <div
              className="w-full rounded-lg bg-error-50 px-4 py-3"
              role="alert"
            >
              <span className="text-caption font-caption text-error-600">
                Error loading insights
              </span>
              <div className="mt-1">
                <span className="text-caption font-caption text-error-600">
                  {insightsData.error}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <MonthAtGlanceInsightsPanel
            insights={insights}
            loading={monthAtGlanceData.loading || insightsData.loading}
          />
        )}
      </div>
    </div>
  );
}
